import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  AutocompleteRenderOptionState,
  Badge,
  Box,
  Checkbox,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DataGridPro,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { getAlarmSeverityColor } from "../lib/AlarmColors";
import { getUnifiedAlarmsQuery } from "../lib/Queries";
import { UnifiedAlarm } from "../lib/Types";
import DataComponentError from "./Shared/DataComponentError";
import Loading from "./Shared/Loading";

type AlertPriority = "Critical" | "High" | "Medium" | "Low";

const priorityOrder: Record<AlertPriority, number> = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3,
};

const defaultSiteStatuses = ["Operating", "Commissioning"];
const emptyAlarms: UnifiedAlarm[] = [];

const formatSiteTime = (date: string, timeZone: string) =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone,
    timeZoneName: "short",
  })
    .format(new Date(date))
    .replace(",", "")
    .replace(" at ", ", ");

export const filterAlerts = (
  rows: UnifiedAlarm[],
  selectedSites: string[],
  selectedSiteStatuses: string[],
) =>
  rows.filter(
    ({ site_name, site_status }) =>
      (selectedSites.length === 0 || selectedSites.includes(site_name)) &&
      (selectedSiteStatuses.length === 0 || selectedSiteStatuses.includes(site_status)),
  );

const renderFilterOption = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: string,
  { selected }: AutocompleteRenderOptionState,
) => (
  <li {...props}>
    <Checkbox checked={selected} sx={{ mr: 1 }} />
    {option}
  </li>
);

const AlarmToolbar = () => (
  <GridToolbarContainer sx={(theme) => ({ minHeight: theme.spacing(5), p: 0 })}>
    <GridToolbarFilterButton />
    <GridToolbarExport
      csvOptions={{
        fileName: `Scale Canopy - Active Alarms ${new Date().toISOString().replaceAll(":", "-")}`,
      }}
      printOptions={{ disableToolbarButton: true }}
    />
    <Box sx={{ flexGrow: 1 }} />
    <GridToolbarQuickFilter
      placeholder="Search by alarm, equipment, or site"
      quickFilterParser={(input) => [input.trim()].filter(Boolean)}
      size="small"
      variant="outlined"
      sx={(theme) => ({ width: { xs: "100%", md: theme.spacing(45) } })}
    />
  </GridToolbarContainer>
);

const AlarmsSummary = () => {
  const theme = useTheme();
  const { data, isLoading, isError } = useQuery(getUnifiedAlarmsQuery());
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedSiteStatuses, setSelectedSiteStatuses] = useState<string[]>(defaultSiteStatuses);
  const alarms = data?.alarms ?? emptyAlarms;
  const autocompleteSx = {
    width: { xs: "100%", md: theme.spacing(45) },
    "& .MuiOutlinedInput-root": {
      bgcolor: "background.paper",
      minHeight: theme.spacing(5),
    },
    "&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root": {
      paddingRight: theme.spacing(7),
    },
  };

  const siteOptions = useMemo(
    () =>
      Array.from(
        new Set(filterAlerts(alarms, [], selectedSiteStatuses).map(({ site_name }) => site_name)),
      ).sort((a, b) => a.localeCompare(b)),
    [alarms, selectedSiteStatuses],
  );
  const siteStatusOptions = useMemo(
    () =>
      Array.from(new Set(alarms.map(({ site_status }) => site_status))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [alarms],
  );
  const sourceOptions = useMemo(
    () =>
      Array.from(new Set(alarms.map(({ source }) => source))).sort((a, b) => a.localeCompare(b)),
    [alarms],
  );
  const filteredAlerts = useMemo(
    () => filterAlerts(alarms, selectedSites, selectedSiteStatuses),
    [alarms, selectedSites, selectedSiteStatuses],
  );

  if (isLoading) return <Loading />;
  if (isError) return <DataComponentError />;

  const columns: GridColDef<UnifiedAlarm>[] = [
    {
      field: "severity",
      headerName: "Prio.",
      width: 80,
      align: "center",
      type: "singleSelect",
      valueOptions: ["Critical", "High", "Medium", "Low"],
      sortComparator: (a, b) =>
        priorityOrder[a as AlertPriority] - priorityOrder[b as AlertPriority],
      getApplyQuickFilterFn: () => null,
      renderCell: ({ value }) => (
        <Badge
          aria-label={`Priority ${value}`}
          variant="dot"
          sx={{
            "& .MuiBadge-badge": {
              width: 12,
              height: 12,
              minWidth: 12,
              borderRadius: "50%",
              backgroundColor: getAlarmSeverityColor(value as AlertPriority),
            },
          }}
        />
      ),
    },
    { field: "name", headerName: "Alarm Name", flex: 1, minWidth: 180 },
    { field: "assembly_name", headerName: "Equipment", width: 131 },
    {
      field: "site_name",
      headerName: "Site",
      flex: 1,
      minWidth: 180,
      type: "singleSelect",
      valueOptions: siteOptions,
      getApplyQuickFilterFn: (filterValue) => (cellValue) =>
        String(cellValue).toLowerCase().includes(filterValue.toLowerCase()),
      renderCell: ({ value }) => (
        <Link
          component={RouterLink}
          to={`/site/${String(value).replaceAll(" ", "-")}`}
          underline="none"
        >
          {value}
        </Link>
      ),
    },
    {
      field: "source",
      headerName: "Source",
      width: 101,
      type: "singleSelect",
      valueOptions: sourceOptions,
      getApplyQuickFilterFn: () => null,
    },
    {
      field: "triggered_at",
      headerName: "Site Time",
      flex: 1,
      minWidth: 190,
      sortComparator: (a, b) => Date.parse(String(a)) - Date.parse(String(b)),
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row }) => formatSiteTime(row.triggered_at, row.site_timezone),
    },
  ];

  return (
    <Box sx={{ ml: 10, px: { xs: 2, md: 10 }, pt: 6, pb: 10 }}>
      <Box sx={{ maxWidth: theme.spacing(127), mx: "auto" }}>
        <Typography variant="h5" color="text.secondary">
          Alarms Summary
        </Typography>
        <Divider sx={{ mt: 2, mb: 3 }} />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          justifyContent="flex-end"
          sx={{ mb: 4.5 }}
        >
          <Autocomplete
            multiple
            disableCloseOnSelect
            openOnFocus
            size="small"
            limitTags={1}
            options={siteOptions}
            value={selectedSites}
            onChange={(_, value) => setSelectedSites(value)}
            renderInput={(params) => <TextField {...params} label="Sites" size="small" />}
            renderOption={renderFilterOption}
            sx={{
              ...autocompleteSx,
              "&:not(.Mui-focused) .MuiAutocomplete-input": {
                pointerEvents: "none",
              },
            }}
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={siteStatusOptions}
            value={selectedSiteStatuses}
            onChange={(_, value) => {
              const compatibleSites = new Set(
                filterAlerts(alarms, [], value).map(({ site_name }) => site_name),
              );
              setSelectedSiteStatuses(value);
              setSelectedSites((sites) => sites.filter((site) => compatibleSites.has(site)));
            }}
            renderInput={(params) => <TextField {...params} label="Site Statuses" size="small" />}
            renderOption={renderFilterOption}
            sx={autocompleteSx}
          />
        </Stack>

        <Box
          sx={{
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
            Active Alarms
          </Typography>
          <DataGridPro
            autoHeight
            columns={columns}
            rows={filteredAlerts}
            rowHeight={42}
            columnHeaderHeight={56}
            disableRowSelectionOnClick
            pagination
            pageSizeOptions={[25, 50, 100]}
            localeText={{
              noResultsOverlayLabel: "No alarms found. Consider adjusting your filters.",
            }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
              sorting: {
                sortModel: [
                  { field: "severity", sort: "asc" },
                  { field: "triggered_at", sort: "desc" },
                ],
              },
            }}
            slots={{ toolbar: AlarmToolbar }}
            sx={{
              border: 0,
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
                typography: "body2",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                typography: "body2",
                fontWeight: "fontWeightMedium",
              },
              "& .MuiDataGrid-iconButtonContainer .MuiBadge-badge": {
                display: "none",
              },
              "& .MuiDataGrid-footerContainer": {
                minHeight: theme.spacing(5.5),
              },
              "& .MuiTablePagination-toolbar": {
                minHeight: theme.spacing(5.5),
                width: "100%",
                left: 0,
                top: 0,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                mb: 0,
              },
              "& .MuiTablePagination-selectIcon": {
                width: theme.spacing(3),
                height: theme.spacing(3),
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AlarmsSummary;
